'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

type TooltipState = {
  text: string;
  shortcut: string | null;
  placement: 'top' | 'bottom' | 'left' | 'right';
  locked: boolean;
  rect: DOMRect;
};

const MARGIN = 8;
const OFFSET = 8;
const GLOBAL_TOOLTIP_ID = 'zenopus-global-tooltip';

const getTooltipTarget = (node: EventTarget | null): Element | null => {
  if (!(node instanceof Element)) return null;
  return node.closest('[data-tooltip]');
};

export default function GlobalTooltip() {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<Element | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const clearTooltip = () => {
      if (targetRef.current) {
        targetRef.current.removeAttribute('aria-describedby');
      }
      targetRef.current = null;
      setTooltip(null);
    };

    const showTooltip = (target: Element | null) => {
      const text = target?.getAttribute('data-tooltip')?.trim();
      if (!target || !text) {
        clearTooltip();
        return;
      }

      if (targetRef.current && targetRef.current !== target) {
        targetRef.current.removeAttribute('aria-describedby');
      }

      const placementAttr = target.getAttribute('data-tooltip-placement')?.trim();
      const locked =
        placementAttr === 'bottom' || placementAttr === 'left' || placementAttr === 'right' || placementAttr === 'top';
      const placement =
        placementAttr === 'bottom' || placementAttr === 'left' || placementAttr === 'right'
          ? placementAttr
          : target.classList.contains('icon-tooltip--bottom')
            ? 'bottom'
            : 'top';
      target.setAttribute('aria-describedby', GLOBAL_TOOLTIP_ID);
      targetRef.current = target;
      setTooltip({
        text,
        shortcut: target.getAttribute('data-tooltip-shortcut')?.trim() || null,
        placement,
        locked,
        rect: target.getBoundingClientRect(),
      });
    };

    const handleMouseOver = (event: MouseEvent) => {
      showTooltip(getTooltipTarget(event.target));
    };

    const hideTooltip = (event: MouseEvent | FocusEvent) => {
      const relatedTarget = event.relatedTarget;
      if (targetRef.current && relatedTarget instanceof Node && targetRef.current.contains(relatedTarget)) {
        return;
      }
      clearTooltip();
    };

    const handleFocusIn = (event: FocusEvent) => {
      showTooltip(getTooltipTarget(event.target));
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', hideTooltip);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', hideTooltip);
    return () => {
      clearTooltip();
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', hideTooltip);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', hideTooltip);
    };
  }, []);

  useEffect(() => {
    if (!tooltip) return;
    const updateRect = () => {
      const target = targetRef.current;
      if (!target) return;
      setTooltip((prev) =>
        prev
          ? {
              ...prev,
              rect: target.getBoundingClientRect(),
            }
          : prev
      );
    };

    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [tooltip]);

  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    setSize({ width: rect.width, height: rect.height });
  }, [tooltip?.text, tooltip?.shortcut]);

  if (!tooltip) return null;

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const rect = tooltip.rect;
  let placement = tooltip.placement;
  const lockedPlacement = tooltip.locked;
  let x =
    placement === 'right'
      ? rect.right + OFFSET
      : placement === 'left'
        ? rect.left - OFFSET
        : rect.left + rect.width / 2;
  let y =
    placement === 'bottom'
      ? rect.bottom + OFFSET
      : placement === 'left' || placement === 'right'
        ? rect.top + rect.height / 2
        : rect.top - OFFSET;

  if (size.height && viewportHeight) {
    if (placement === 'top' && y - size.height < MARGIN) {
      if (lockedPlacement) {
        y = MARGIN + size.height;
      } else {
        placement = 'bottom';
        y = rect.bottom + OFFSET;
      }
    } else if (placement === 'bottom' && y + size.height > viewportHeight - MARGIN) {
      if (lockedPlacement) {
        y = viewportHeight - MARGIN - size.height;
      } else {
        placement = 'top';
        y = rect.top - OFFSET;
      }
    }
  }

  if (size.width && viewportWidth) {
    if (placement === 'right') {
      if (x + size.width > viewportWidth - MARGIN) {
        if (lockedPlacement) {
          x = viewportWidth - MARGIN - size.width;
        } else {
          placement = 'left';
          x = rect.left - OFFSET;
        }
      }
    } else if (placement === 'left') {
      if (x - size.width < MARGIN) {
        if (lockedPlacement) {
          x = MARGIN + size.width;
        } else {
          placement = 'right';
          x = rect.right + OFFSET;
        }
      }
    } else {
      const half = size.width / 2;
      x = Math.min(Math.max(x, MARGIN + half), viewportWidth - MARGIN - half);
    }
  }

  if (size.height && viewportHeight && (placement === 'left' || placement === 'right')) {
    const halfHeight = size.height / 2;
    y = Math.min(Math.max(y, MARGIN + halfHeight), viewportHeight - MARGIN - halfHeight);
  }

  return (
    <div
      id={GLOBAL_TOOLTIP_ID}
      ref={tooltipRef}
      className={`global-tooltip ${
        placement === 'bottom'
          ? 'global-tooltip--bottom'
          : placement === 'left'
            ? 'global-tooltip--left'
            : placement === 'right'
              ? 'global-tooltip--right'
              : ''
      }`}
      style={{ left: x, top: y }}
      role="tooltip"
    >
      <span>{tooltip.text}</span>
      {tooltip.shortcut ? <span className="global-tooltip__shortcut">{tooltip.shortcut}</span> : null}
    </div>
  );
}
